// src/pages/contact/ContactPage.tsx

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    FaEnvelope,
    FaPhone,
    FaMapMarkerAlt,
    FaClock,
    FaReply,
    FaInstagram,
    FaYoutube,
    FaTiktok,
    FaShieldAlt,
    FaFileAlt,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { CONTACT_KEYS, getContactInfo, ContactInfoMap } from "../../services/adminContactService";
import "./ContactPage.css";

// ── Icon mapping: contact key → icon component ────────────────────────────────

const ICONS: Record<string, React.ReactNode> = {
    [CONTACT_KEYS.EMAIL_GENERAL]:     <FaEnvelope />,
    [CONTACT_KEYS.EMAIL_ADVERTISING]:  <FaEnvelope />,
    [CONTACT_KEYS.EMAIL_COMPLAINT]:    <FaEnvelope />,
    [CONTACT_KEYS.EMAIL_PRESS]:        <FaEnvelope />,
    [CONTACT_KEYS.EMAIL_DEVELOPER]:    <FaEnvelope />,
    [CONTACT_KEYS.PHONE]:              <FaPhone />,
    [CONTACT_KEYS.ADDRESS]:            <FaMapMarkerAlt />,
    [CONTACT_KEYS.WORKING_HOURS]:      <FaClock />,
    [CONTACT_KEYS.RESPONSE_TIME]:      <FaReply />,
    [CONTACT_KEYS.SOCIAL_X]:           <FaXTwitter />,
    [CONTACT_KEYS.SOCIAL_INSTAGRAM]:   <FaInstagram />,
    [CONTACT_KEYS.SOCIAL_YOUTUBE]:     <FaYoutube />,
    [CONTACT_KEYS.SOCIAL_TIKTOK]:      <FaTiktok />,
    [CONTACT_KEYS.URL_PRIVACY]:        <FaShieldAlt />,
    [CONTACT_KEYS.URL_TERMS]:          <FaFileAlt />,
};

// ── Helper: determine whether a value is a URL (link) or plain text ───────────

const isUrl = (key: string): boolean =>
    key.startsWith("contact.social.") || key.startsWith("contact.url.");

const isEmail = (key: string): boolean => key.startsWith("contact.email.");

// ── Ordered display sections ───────────────────────────────────────────────────

interface Section {
    titleKey: string;
    keys: string[];
}

const SECTIONS: Section[] = [
    {
        titleKey: "contact_section_emails",
        keys: [
            CONTACT_KEYS.EMAIL_GENERAL,
            CONTACT_KEYS.EMAIL_ADVERTISING,
            CONTACT_KEYS.EMAIL_COMPLAINT,
            CONTACT_KEYS.EMAIL_PRESS,
            CONTACT_KEYS.EMAIL_DEVELOPER,
        ],
    },
    {
        titleKey: "contact_section_address",
        keys: [CONTACT_KEYS.PHONE, CONTACT_KEYS.ADDRESS],
    },
    {
        titleKey: "contact_section_info",
        keys: [CONTACT_KEYS.WORKING_HOURS, CONTACT_KEYS.RESPONSE_TIME],
    },
    {
        titleKey: "contact_section_social",
        keys: [
            CONTACT_KEYS.SOCIAL_X,
            CONTACT_KEYS.SOCIAL_INSTAGRAM,
            CONTACT_KEYS.SOCIAL_YOUTUBE,
            CONTACT_KEYS.SOCIAL_TIKTOK,
        ],
    },
    {
        titleKey: "contact_section_legal",
        keys: [CONTACT_KEYS.URL_PRIVACY, CONTACT_KEYS.URL_TERMS],
    },
];

// ── Component ──────────────────────────────────────────────────────────────────

export const ContactPage: React.FC = () => {
    const { t } = useTranslation();
    const [info, setInfo] = useState<ContactInfoMap>({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getContactInfo()
            .then(setInfo)
            .catch(() => setInfo({}))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="contact-page">
                <div className="contact-loading">{t("loading", "Yükleniyor...")}</div>
            </div>
        );
    }

    // Determine which sections have at least one configured value
    const visibleSections = SECTIONS.filter((section) =>
        section.keys.some((key) => Boolean(info[key as keyof ContactInfoMap]))
    );

    if (visibleSections.length === 0) {
        return (
            <div className="contact-page">
                <div className="contact-empty">{t("contact_empty", "Henüz iletişim bilgisi girilmemiş.")}</div>
            </div>
        );
    }

    return (
        <div className="contact-page">
            <div className="contact-header">
                <h1 className="contact-title">{t("contact_page_title", "İletişim Adreslerimiz")}</h1>
                <p className="contact-subtitle">{t("contact_page_subtitle", "Aşağıdaki kanallar üzerinden bize ulaşabilirsiniz.")}</p>
            </div>

            <div className="contact-sections">
                {visibleSections.map((section) => {
                    // Only render keys that have a value
                    const activeKeys = section.keys.filter(
                        (key) => Boolean(info[key as keyof ContactInfoMap])
                    );

                    return (
                        <div key={section.titleKey} className="contact-section">
                            <h2 className="contact-section-title">{t(section.titleKey)}</h2>

                            <div className="contact-items">
                                {activeKeys.map((key) => {
                                    const value = info[key as keyof ContactInfoMap] as string;

                                    return (
                                        <div key={key} className="contact-item">
                                            <span className="contact-item-icon">{ICONS[key]}</span>

                                            <div className="contact-item-body">
                                                <span className="contact-item-label">{t(`contact_label_${key.replace(/\./g, "_")}`)}</span>

                                                {isEmail(key) ? (
                                                    <a
                                                        href={`mailto:${value}`}
                                                        className="contact-item-value contact-item-link"
                                                    >
                                                        {value}
                                                    </a>
                                                ) : isUrl(key) ? (
                                                    <a
                                                        href={value}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="contact-item-value contact-item-link"
                                                    >
                                                        {value}
                                                    </a>
                                                ) : (
                                                    <span className="contact-item-value">{value}</span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};